import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { OAuthErrorEvent, OAuthService, TokenResponse } from 'angular-oauth2-oidc';
import { BehaviorSubject, ReplaySubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AuthService } from './auth.service';

@Injectable()
export class KeycloakAuthService extends AuthService {
  private readonly isAuthenticatedSubject$ = new BehaviorSubject<boolean>(false);
  public isAuthenticated$ = this.isAuthenticatedSubject$.asObservable();

  private readonly isDoneLoadingSubject$ = new ReplaySubject<boolean>();
  public isDoneLoading$ = this.isDoneLoadingSubject$.asObservable();

  constructor(
    private readonly oauthService: OAuthService,
    private readonly router: Router
  ) {
    super();

    this.oauthService.events.subscribe(event => {
      if (event instanceof OAuthErrorEvent) {
        console.error('OAuthErrorEvent Object:', event);
        if (event.type === 'invalid_nonce_in_state') {
          this.logout();
        }
      } else {
        console.warn('OAuthEvent Object:', event);
      }
    });

    window.addEventListener('storage', event => {
      if (event.key !== 'access_token' && event.key !== null) {
        return;
      }

      console.warn(
        'Noticed changes to access_token (most likely from another tab), updating isAuthenticated'
      );
      this.isAuthenticatedSubject$.next(this.oauthService.hasValidAccessToken());

      if (!this.oauthService.hasValidAccessToken()) {
        window.location.reload();
      }
    });

    this.oauthService.events.subscribe(_ => {
      this.isAuthenticatedSubject$.next(this.oauthService.hasValidAccessToken());
    });

    this.oauthService.events
      .pipe(filter(e => ['token_received'].includes(e.type)))
      .subscribe(e => this.oauthService.loadUserProfile());

    this.oauthService.events
      .pipe(filter(e => ['session_terminated', 'session_error'].includes(e.type)))
      .subscribe(e => {
        window.location.reload();
      });

    this.oauthService.setupAutomaticSilentRefresh();
  }

  public runInitialLoginSequence(): Promise<void> {
    const timeout = 1000;
    return this.oauthService
      .loadDiscoveryDocument()
      .then(() => new Promise<void>(resolve => setTimeout(() => resolve(), timeout)))
      .then(() => this.oauthService.tryLogin())
      .then(() => {
        if (this.oauthService.hasValidAccessToken()) {
          return Promise.resolve();
        }

        return this.oauthService
          .refreshToken()
          .then(() => Promise.resolve())
          .catch(result => {
            const errorResponsesRequiringUserInteraction = [
              'interaction_required',
              'login_required',
              'account_selection_required',
              'consent_required',
            ];

            if (
              result &&
              result.reason &&
              errorResponsesRequiringUserInteraction.indexOf(result.reason.error) >= 0
            ) {
              this.login();
              console.warn(
                'User interaction is needed to log in, we will wait for the user to manually log in.'
              );
              return Promise.resolve();
            }

            return Promise.reject(result);
          });
      })
      .then(() => {
        this.isDoneLoadingSubject$.next(true);

        if (
          this.oauthService.state &&
          this.oauthService.state !== 'undefined' &&
          this.oauthService.state !== 'null'
        ) {
          let stateUrl = this.oauthService.state;
          if (stateUrl.startsWith('/') === false) {
            stateUrl = decodeURIComponent(stateUrl);
          }
          this.router.navigateByUrl(stateUrl);
        }
      })
      .catch(() => this.isDoneLoadingSubject$.next(true));
  }

  public login(): void {
    this.oauthService.initLoginFlow(this.router.url);
  }

  public logout(): void {
    this.oauthService.logOut();
  }

  public refresh(): Promise<TokenResponse> {
    return this.oauthService.refreshToken();
  }

  public isLogged(): boolean {
    return (
      !!this.accessToken && this.oauthService.hasValidAccessToken() && this.oauthService.hasValidIdToken()
    );
  }

  private navigateToLoginPage(): void {
    this.router.navigateByUrl('/sign-in');
  }

  public get accessToken(): string {
    return this.oauthService.getAccessToken();
  }

  public get refreshToken(): string {
    return this.oauthService.getRefreshToken();
  }
}
